import { useState, type FormEvent } from "react";
import { Check, Copy, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { describeAuthError, useAuth } from "@/hooks/use-auth";
import { appEnv, isProduction, projectId } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BrandMark } from "@/components/layout/BrandMark";
import { GoogleMark } from "@/components/layout/GoogleMark";
import { Separator } from "@/components/ui/separator";

export default function Login() {
  const { user, isAdmin, signIn, signInWithGoogle, signOut, resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Autenticado e sem o claim: a conta existe, mas não é de administrador.
  // Dizer isso em vez de repetir o formulário evita a pessoa tentar a mesma
  // senha cinco vezes achando que errou.
  const authenticatedWithoutClaim = Boolean(user) && !isAdmin;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (error) {
      toast.error(describeAuthError(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    if (busy) return;
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      toast.error(describeAuthError(error));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Copia o UID para quem vai cadastrá-lo na allowlist.
   *
   * O `navigator.clipboard` não existe fora de contexto seguro e pode ser
   * negado por permissão, então a falha cai num aviso em vez de quebrar: o UID
   * continua à vista para ser selecionado à mão.
   */
  async function handleCopyUid() {
    if (!user?.uid) return;
    try {
      await navigator.clipboard.writeText(user.uid);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Selecione o UID e copie à mão.");
    }
  }

  async function handleReset() {
    const target = email.trim();
    if (!target) {
      toast.error("Informe o e-mail para receber o link de redefinição.");
      return;
    }
    try {
      await resetPassword(target);
      toast.success("Link de redefinição enviado para o e-mail informado.");
    } catch (error) {
      toast.error(describeAuthError(error));
    }
  }

  return (
    <div className="flex min-h-viewport items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm space-y-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <BrandMark className="size-16" />
          <div>
            <h1 className="text-h5 font-black tracking-tight">Zé Padoca Manager</h1>
            <p className="text-small text-muted-foreground">Plataforma de gestão</p>
          </div>
          {!isProduction ? (
            <Badge variant="warning" className="uppercase">
              {appEnv}
            </Badge>
          ) : null}
        </div>

        {authenticatedWithoutClaim ? (
          <Card>
            <CardHeader>
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 size-5 shrink-0 text-state-warning" />
                <div className="min-w-0">
                  <CardTitle>Aguardando liberação de acesso</CardTitle>
                  <CardDescription className="mt-1">
                    A conta {user?.email} entrou e já existe no projeto, mas ainda não está na
                    lista de administradores.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* O UID fica aqui porque é exatamente o que falta para liberar o
                  acesso, e caçá-lo no Console depois é o passo chato do
                  processo. Numa conta Google, esta tela é a PRIMEIRA vez que
                  ele existe: a conta nasce no momento da entrada. */}
              <div className="space-y-1.5">
                <Label htmlFor="uid">UID desta conta</Label>
                <div className="flex gap-2">
                  <Input
                    id="uid"
                    readOnly
                    value={user?.uid ?? ""}
                    className="font-mono text-caption"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Copiar o UID"
                    onClick={() => void handleCopyUid()}
                  >
                    {copied ? <Check className="text-state-ok" /> : <Copy />}
                  </Button>
                </div>
                <p className="text-caption text-muted-foreground">
                  Quem administra o projeto acrescenta este UID em{" "}
                  <span className="font-mono">_meta/adminAllowList</span>, no Firestore, com o
                  e-mail como valor. Depois disso, basta entrar de novo.
                </p>
              </div>

              <Button variant="outline" className="w-full" onClick={() => void signOut()}>
                Entrar com outra conta
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Entrar</CardTitle>
              <CardDescription>Acesso restrito a administradores.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* O Google vem PRIMEIRO porque é o caminho de quem administra o
                  projeto: uma conta Google não tem senha no Firebase Auth, e
                  nem dá para criá-la à mão no Console. A entrada com ela é o
                  que faz a conta existir. */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={busy}
                onClick={() => void handleGoogle()}
              >
                {busy ? <Loader2 className="animate-spin" /> : <GoogleMark className="size-4" />}
                Entrar com o Google
              </Button>

              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-caption text-muted-foreground">ou</span>
                <Separator className="flex-1" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@zepadoca.com.br"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? <Loader2 className="animate-spin" /> : null}
                  Entrar
                </Button>
                <Button type="button" variant="link" className="w-full" onClick={() => void handleReset()}>
                  Esqueci minha senha
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-caption text-muted-foreground">Projeto: {projectId}</p>
      </div>
    </div>
  );
}
